import { cva, type VariantProps } from "class-variance-authority";
import { type TwcComponentProps, twc } from "../../utils";

const skeletonBase = "bg-secondary/60 animate-pulse";

const skeletonLine = cva(skeletonBase, {
  variants: {
    $size: {
      caption: "h-3 rounded-full",
      text: "h-4 rounded-full",
      title: "h-5 rounded-md",
      heading: "h-7 rounded-md",
    },
    $width: {
      xs: "w-1/4",
      sm: "w-2/5",
      md: "w-3/5",
      lg: "w-4/5",
      full: "w-full",
    },
  },
  defaultVariants: {
    $size: "text",
    $width: "full",
  },
});

type SkeletonLineProps = TwcComponentProps<"div"> & VariantProps<typeof skeletonLine>;

export const SkeletonLine = twc.div<SkeletonLineProps>(({ $size, $width }) =>
  skeletonLine({ $size, $width }),
);

const skeletonPill = cva(skeletonBase, {
  variants: {
    $size: {
      sm: "h-7 rounded-full",
      md: "h-9 rounded-full",
      lg: "h-11 rounded-full",
    },
    $width: {
      xs: "w-12",
      sm: "w-20",
      md: "w-28",
      lg: "w-36",
      xl: "w-44",
    },
  },
  defaultVariants: {
    $size: "md",
    $width: "md",
  },
});

type SkeletonPillProps = TwcComponentProps<"div"> & VariantProps<typeof skeletonPill>;

export const SkeletonPill = twc.div<SkeletonPillProps>(({ $size, $width }) =>
  skeletonPill({ $size, $width }),
);

/**
 * Chip-shaped skeleton — matches the rendered height + radius of the real
 * @health/ui Chip (rounded-2xl, ~52px once 16px text-size-chips + p-4 are
 * applied). All chips render at the same height so hydration doesn't jolt
 * the layout.
 */
export const SkeletonChip = twc.div`${skeletonBase} h-[52px] w-40 rounded-2xl`;

const skeletonBlock = cva(`${skeletonBase} w-full`, {
  variants: {
    $h: {
      xs: "h-8",
      sm: "h-12",
      md: "h-20",
      lg: "h-32",
      xl: "h-44",
    },
    $radius: {
      sm: "rounded-lg",
      md: "rounded-xl",
      lg: "rounded-2xl",
    },
  },
  defaultVariants: {
    $h: "md",
    $radius: "md",
  },
});

type SkeletonBlockProps = TwcComponentProps<"div"> & VariantProps<typeof skeletonBlock>;

export const SkeletonBlock = twc.div<SkeletonBlockProps>(({ $h, $radius }) =>
  skeletonBlock({ $h, $radius }),
);

const skeletonAvatar = cva(`${skeletonBase} rounded-full shrink-0`, {
  variants: {
    $size: {
      sm: "h-8 w-8",
      md: "h-10 w-10",
      lg: "h-14 w-14",
    },
  },
  defaultVariants: {
    $size: "md",
  },
});

type SkeletonAvatarProps = TwcComponentProps<"div"> & VariantProps<typeof skeletonAvatar>;

export const SkeletonAvatar = twc.div<SkeletonAvatarProps>(({ $size }) =>
  skeletonAvatar({ $size }),
);

/** Card surface mirroring the WidgetCard look used by real widgets. */
export const SkeletonCard = twc.div`bg-widget-bg/40 rounded-2xl shadow-sm p-4`;

/** Stack helpers — keep variant files tiny. */
export const SkeletonStack = twc.div`w-full space-y-3`;
export const SkeletonRow = twc.div`flex items-center gap-2`;
export const SkeletonWrap = twc.div`flex flex-wrap gap-2`;
